import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom, type PropsOf } from '@blaze-runtime/engine-domain';
type TextInputElement = CanvasElementFrom<'textInput'>;
type TextInputProps = PartialDeep<PropsOf<'textInput'>>;

function transformDropdownToTextInput(source: CanvasElementFrom<'dropdown'>): TextInputElement {
  const props: PartialDeep<PropsOf<'dropdown'>> = source.props || {};
  const newProps: TextInputProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: { horizontal: props.content?.title?.align },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: props.content?.menu?.placeholder,
        format: undefined, // no mapping
        readonly: undefined, // no mapping
        precision: undefined, // no mapping
        currency: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        value: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformImageToTextInput(source: CanvasElementFrom<'image'>): TextInputElement {
  const props: PartialDeep<PropsOf<'image'>> = source.props || {};
  const newProps: TextInputProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: { horizontal: props.content?.title?.align },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: undefined, // no mapping
        format: undefined, // no mapping
        readonly: props.content?.options?.readonly,
        precision: undefined, // no mapping
        currency: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: undefined, // no mapping
      },
      input: {
        value: undefined, // no mapping
        fill: undefined, // no mapping
        border: props.style?.outline?.color,
        radius: props.style?.outline?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformRadioToTextInput(source: CanvasElementFrom<'radio'>): TextInputElement {
  const props: PartialDeep<PropsOf<'radio'>> = source.props || {};
  const newProps: TextInputProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: { horizontal: props.content?.title?.align },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: undefined, // no mapping
        format: undefined, // no mapping
        readonly: props.content?.radio?.readonly,
        precision: undefined, // no mapping
        currency: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        value: props.style?.radio?.label,
        fill: props.style?.radio?.fill,
        border: props.style?.radio?.border,
        radius: undefined, // no mapping
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTagsToTextInput(source: CanvasElementFrom<'tags'>): TextInputElement {
  const props: PartialDeep<PropsOf<'tags'>> = source.props || {};
  const newProps: TextInputProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: { horizontal: props.content?.title?.align },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: props.content?.tags?.placeholder,
        format: undefined, // no mapping
        readonly: props.content?.options?.readonly,
        precision: undefined, // no mapping
        currency: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        value: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformUploadFileToTextInput(source: CanvasElementFrom<'uploadFile'>): TextInputElement {
  const props: PartialDeep<PropsOf<'uploadFile'>> = source.props || {};
  const newProps: TextInputProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: { horizontal: props.content?.title?.align },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: undefined, // no mapping
        format: undefined, // no mapping
        readonly: props.content?.button?.readonly,
        precision: undefined, // no mapping
        currency: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: undefined, // no mapping
      },
      input: {
        value: props.style?.uploaded?.label,
        fill: props.style?.uploaded?.fill,
        border: props.style?.uploaded?.outline,
        radius: props.style?.button?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTextFieldToTextInput(source: CanvasElementFrom<'textField'>): TextInputElement {
  const props: PartialDeep<PropsOf<'textField'>> = source.props || {};
  const newProps: TextInputProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: props.content?.input?.placeholder,
        format: props.content?.input?.format,
        readonly: false,
        precision: props.content?.input?.precision,
        currency: props.content?.input?.currency,
      },
      container: {
        overflow: props?.content?.container?.overflow,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      inputFont: {
        style: props.style?.inputFont?.style,
        fontFamily: props.style?.inputFont?.fontFamily,
        fontSize: props.style?.inputFont?.fontSize,
        lineHeight: props.style?.inputFont?.lineHeight,
      },
      input: {
        value: props.style?.input?.value,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

export const transformToTextInput = {
  transformDropdownToTextInput,
  transformImageToTextInput,
  transformRadioToTextInput,
  transformTagsToTextInput,
  transformUploadFileToTextInput,
  transformTextFieldToTextInput,
};
